import React, { useState, useEffect } from 'react';
import {
    Calendar, Clock, User, Users, Check, X, Mail, Phone,
    ChevronLeft, ChevronRight, MessageSquare, Euro, TrendingUp,
    AlertCircle, Loader2, Settings, LogOut, Bell, FileText,
    CheckCircle, XCircle, DollarSign, CalendarDays, RefreshCw
} from 'lucide-react';
import {
    collection, query, where, getDocs, doc, updateDoc, getDoc,
    onSnapshot, orderBy, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AvailabilitySettings from '../components/AvailabilitySettings';

const PsychologistDashboard = () => {
    const { currentUser, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('pending');
    const [isLoading, setIsLoading] = useState(true);
    const [psychologistData, setPsychologistData] = useState(null);

    // Date
    const [pendingBookings, setPendingBookings] = useState([]);
    const [confirmedBookings, setConfirmedBookings] = useState([]);
    const [allBookings, setAllBookings] = useState([]);
    const [stats, setStats] = useState({ pending: 0, confirmed: 0, paid: 0, done: 0, earnings: 0 });

    // Calendar
    const [currentWeek, setCurrentWeek] = useState(getMonday(new Date()));

    // Acțiuni
    const [actionLoading, setActionLoading] = useState(null);

    function getMonday(date) {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    }

    function formatDate(dateStr) {
        return new Date(dateStr).toLocaleDateString('ro-RO', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
    }

    function formatShortDate(dateStr) {
        return new Date(dateStr).toLocaleDateString('ro-RO', {
            day: 'numeric',
            month: 'short'
        });
    }

    // Încarcă datele psihologului
    useEffect(() => {
        if (!currentUser) return;

        const loadPsychologistData = async () => {
            // Caută psihologul după email
            const psychSnap = await getDocs(
                query(collection(db, 'psychologists'), where('email', '==', currentUser.email))
            );

            if (!psychSnap.empty) {
                const psychDoc = psychSnap.docs[0];
                setPsychologistData({ id: psychDoc.id, ...psychDoc.data() });
            }
        };

        loadPsychologistData();
    }, [currentUser]);

    // Ascultă booking-urile în timp real
    useEffect(() => {
        if (!psychologistData?.id) return;

        const bookingsRef = collection(db, 'bookings');
        const q = query(
            bookingsRef,
            where('psychologistId', '==', psychologistData.id),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const bookings = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate?.() || new Date()
            }));

            setAllBookings(bookings);
            setPendingBookings(bookings.filter(b => b.status === 'pending'));
            setConfirmedBookings(bookings.filter(b => ['confirmed', 'paid'].includes(b.status)));

            // Statistici
            const pending = bookings.filter(b => b.status === 'pending').length;
            const confirmed = bookings.filter(b => b.status === 'confirmed').length;
            const paid = bookings.filter(b => b.status === 'paid').length;
            const done = bookings.filter(b => b.status === 'done').length;
            const earnings = bookings
                .filter(b => b.status === 'paid' || b.status === 'done')
                .reduce((sum, b) => sum + (b.sessionPrice || 0), 0);

            setStats({ pending, confirmed, paid, done, earnings });
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [psychologistData]);

    // Acțiuni pe booking-uri
    const handleConfirm = async (bookingId) => {
        setActionLoading(bookingId);
        try {
            // Actualizează status în Firestore
            await updateDoc(doc(db, 'bookings', bookingId), {
                status: 'confirmed',
                confirmedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            // Trimite email cu link de plată
            const functions = getFunctions();
            const sendPaymentLink = httpsCallable(functions, 'sendPaymentLink');
            await sendPaymentLink({ bookingId });

            console.log('Booking confirmat!');
        } catch (err) {
            console.error('Eroare la confirmare:', err);
            alert('Eroare la confirmare. Încearcă din nou.');
        }
        setActionLoading(null);
    };

    const handleDecline = async (bookingId) => {
        if (!window.confirm('Ești sigur(ă) că vrei să refuzi această cerere?')) return;

        setActionLoading(bookingId);
        try {
            await updateDoc(doc(db, 'bookings', bookingId), {
                status: 'declined',
                declinedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            const functions = getFunctions();
            const sendDeclineEmail = httpsCallable(functions, 'sendDeclineEmail');
            await sendDeclineEmail({ bookingId });

            console.log('Booking refuzat!');
        } catch (err) {
            console.error('Eroare la refuzare:', err);
        }
        setActionLoading(null);
    };

    const handleMarkDone = async (bookingId) => {
        setActionLoading(bookingId);
        try {
            await updateDoc(doc(db, 'bookings', bookingId), {
                status: 'done',
                completedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
        } catch (err) {
            console.error('Eroare:', err);
        }
        setActionLoading(null);
    };

    // Calendar helpers
    const getWeekDays = () => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(currentWeek);
            day.setDate(currentWeek.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const getBookingsForDay = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return allBookings.filter(b =>
            b.date === dateStr && ['confirmed', 'paid', 'done'].includes(b.status)
        );
    };

    const statusColors = {
        pending: 'bg-amber-100 text-amber-700 border-amber-200',
        confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
        paid: 'bg-green-100 text-green-700 border-green-200',
        done: 'bg-slate-100 text-slate-600 border-slate-200',
        declined: 'bg-red-100 text-red-700 border-red-200',
        cancelled: 'bg-red-100 text-red-700 border-red-200'
    };

    const statusLabels = {
        pending: 'În așteptare',
        confirmed: 'Confirmată',
        paid: 'Plătită',
        done: 'Finalizată',
        declined: 'Refuzată',
        cancelled: 'Anulată'
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="animate-spin text-purple-600 mx-auto mb-4" size={48} />
                    <p className="text-slate-600">Se încarcă dashboard-ul...</p>
                </div>
            </div>
        );
    }

    if (!psychologistData) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
                    <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Acces restricționat</h2>
                    <p className="text-slate-600 mb-4">
                        Nu ai permisiuni de psiholog sau contul nu este configurat corect.
                    </p>
                    <button
                        onClick={logout}
                        className="text-purple-600 font-semibold hover:underline"
                    >
                        Deconectare
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-purple-200">
                                <img
                                    src={psychologistData.photo}
                                    alt={psychologistData.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
                                <p className="text-sm text-slate-600">{psychologistData.name}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {stats.pending > 0 && (
                                <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                                    <Bell size={16} />
                                    {stats.pending} cereri noi
                                </div>
                            )}
                            <button
                                onClick={logout}
                                className="text-slate-600 hover:text-slate-900 p-2"
                            >
                                <LogOut size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Stats */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-amber-600 mb-2">
                            <AlertCircle size={24} />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{stats.pending}</div>
                        <div className="text-sm text-slate-600">În așteptare</div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-blue-600 mb-2">
                            <Clock size={24} />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{stats.confirmed}</div>
                        <div className="text-sm text-slate-600">Confirmate</div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-green-600 mb-2">
                            <CheckCircle size={24} />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{stats.paid}</div>
                        <div className="text-sm text-slate-600">Plătite</div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-slate-600 mb-2">
                            <FileText size={24} />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{stats.done}</div>
                        <div className="text-sm text-slate-600">Finalizate</div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <div className="text-purple-600 mb-2">
                            <Euro size={24} />
                        </div>
                        <div className="text-2xl font-bold text-slate-900">{stats.earnings}€</div>
                        <div className="text-sm text-slate-600">Încasări</div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex gap-2 border-b border-slate-200">
                    {[
                        { id: 'pending', label: 'Cereri noi', icon: Bell, count: stats.pending },
                        { id: 'calendar', label: 'Calendar', icon: CalendarDays },
                        { id: 'all', label: 'Toate', icon: FileText },
                        { id: 'settings', label: 'Program', icon: Settings }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 font-semibold transition-colors border-b-2 -mb-px ${activeTab === tab.id
                                    ? 'text-purple-600 border-purple-600'
                                    : 'text-slate-600 border-transparent hover:text-slate-900'
                                }`}
                        >
                            <tab.icon size={18} />
                            {tab.label}
                            {tab.count > 0 && (
                                <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                {/* Tab: Cereri noi */}
                {activeTab === 'pending' && (
                    <div className="space-y-4">
                        {pendingBookings.length === 0 ? (
                            <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
                                <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
                                <h3 className="text-xl font-bold text-slate-900 mb-2">Totul e la zi!</h3>
                                <p className="text-slate-600">Nu ai cereri în așteptare.</p>
                            </div>
                        ) : (
                            pendingBookings.map(booking => (
                                <div key={booking.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${statusColors[booking.status]}`}>
                                                    {statusLabels[booking.status]}
                                                </span>
                                                <span className="text-sm text-slate-500">
                                                    #{booking.id.slice(-6).toUpperCase()}
                                                </span>
                                            </div>

                                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                                                {booking.sessionTypeName}
                                            </h3>

                                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                                                        <User size={16} />
                                                        <span className="font-medium">{booking.clientName}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                                                        <Mail size={16} />
                                                        <a href={`mailto:${booking.clientEmail}`} className="text-purple-600 hover:underline">
                                                            {booking.clientEmail}
                                                        </a>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-slate-600">
                                                        <Phone size={16} />
                                                        <a href={`tel:${booking.clientPhone}`} className="text-purple-600 hover:underline">
                                                            {booking.clientPhone}
                                                        </a>
                                                    </div>
                                                </div>

                                                <div>
                                                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                                                        <Calendar size={16} />
                                                        <span>{formatDate(booking.date)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                                                        <Clock size={16} />
                                                        <span>{booking.time} ({booking.sessionDuration} min)</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                                                        <Euro size={16} />
                                                        <span>{booking.sessionPrice}€</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {booking.notes && (
                                                <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
                                                    <div className="flex items-start gap-2">
                                                        <MessageSquare size={16} className="text-slate-500 mt-0.5" />
                                                        <p className="text-sm text-slate-700">{booking.notes}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
                                        <button
                                            onClick={() => handleConfirm(booking.id)}
                                            disabled={actionLoading === booking.id}
                                            className="flex-1 bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            {actionLoading === booking.id ? (
                                                <Loader2 className="animate-spin" size={20} />
                                            ) : (
                                                <Check size={20} />
                                            )}
                                            Confirmă & Trimite link plată
                                        </button>
                                        <button
                                            onClick={() => handleDecline(booking.id)}
                                            disabled={actionLoading === booking.id}
                                            className="px-6 py-3 border-2 border-red-200 text-red-600 rounded-xl font-semibold hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                                        >
                                            <X size={20} />
                                            Refuză
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Tab: Calendar */}
                {activeTab === 'calendar' && (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        {/* Calendar header */}
                        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
                            <button
                                onClick={() => {
                                    const prev = new Date(currentWeek);
                                    prev.setDate(prev.getDate() - 7);
                                    setCurrentWeek(prev);
                                }}
                                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </button>

                            <div className="text-center">
                                <div className="font-bold text-slate-900">
                                    {formatShortDate(currentWeek.toISOString())} - {formatShortDate(new Date(currentWeek.getTime() + 6 * 24 * 60 * 60 * 1000).toISOString())}
                                </div>
                                <button
                                    onClick={() => setCurrentWeek(getMonday(new Date()))}
                                    className="text-sm text-purple-600 hover:underline"
                                >
                                    Săptămâna curentă
                                </button>
                            </div>

                            <button
                                onClick={() => {
                                    const next = new Date(currentWeek);
                                    next.setDate(next.getDate() + 7);
                                    setCurrentWeek(next);
                                }}
                                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>

                        {/* Calendar grid */}
                        <div className="grid grid-cols-7 divide-x divide-slate-200">
                            {getWeekDays().map((day, idx) => {
                                const dayBookings = getBookingsForDay(day);
                                const isToday = day.toDateString() === new Date().toDateString();
                                const dayNames = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'];

                                return (
                                    <div key={idx} className="min-h-[200px]">
                                        <div className={`p-3 text-center border-b border-slate-200 ${isToday ? 'bg-purple-50' : 'bg-slate-50'}`}>
                                            <div className="text-xs text-slate-500 uppercase">{dayNames[idx]}</div>
                                            <div className={`text-lg font-bold ${isToday ? 'text-purple-600' : 'text-slate-900'}`}>
                                                {day.getDate()}
                                            </div>
                                        </div>
                                        <div className="p-2 space-y-2">
                                            {dayBookings.map(booking => (
                                                <div
                                                    key={booking.id}
                                                    className={`p-2 rounded-lg text-xs border ${statusColors[booking.status]}`}
                                                >
                                                    <div className="font-bold">{booking.time}</div>
                                                    <div className="truncate">{booking.clientName}</div>
                                                    <div className="truncate opacity-75">{booking.sessionTypeName}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Tab: Toate booking-urile */}
                {activeTab === 'all' && (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Client</th>
                                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Ședință</th>
                                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Data</th>
                                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Preț</th>
                                    <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Acțiuni</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {allBookings.map(booking => (
                                    <tr key={booking.id} className="hover:bg-slate-50">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">{booking.clientName}</div>
                                            <div className="text-sm text-slate-500">{booking.clientEmail}</div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-700">{booking.sessionTypeName}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-slate-900">{booking.date}</div>
                                            <div className="text-sm text-slate-500">{booking.time}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${statusColors[booking.status]}`}>
                                                {statusLabels[booking.status]}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-slate-900">{booking.sessionPrice}€</td>
                                        <td className="px-4 py-3">
                                            {booking.status === 'paid' && (
                                                <button
                                                    onClick={() => handleMarkDone(booking.id)}
                                                    disabled={actionLoading === booking.id}
                                                    className="text-sm text-purple-600 hover:underline font-medium"
                                                >
                                                    Marchează finalizată
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Tab: Setări program */}
                {activeTab === 'settings' && (
                    <div className="bg-white rounded-xl p-6 border border-slate-200">
                        <AvailabilitySettings
                            psychologistId={psychologistData?.id}
                            onSave={() => console.log('Program salvat!')}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PsychologistDashboard;